import { setHorizontalSrollEvent, setResizeEvent, getTabWidth, PC_BREAK_POINT } from './tabEvent';
import { depth4UrListType } from '../interface/ITab';

/**
 * @alias Tab
 * @category tab
 * @example
 * if (window.ncui && window.ncui.tab) {
 *    new window.ncui.tab();
 * }
 */
export default class Index {
    // depth3
    private depth3Tab: HTMLDivElement | null;
    private depth3List: HTMLDivElement | null;
    private depth3ListId: string | null | undefined;
    private depth3TabListItems: NodeListOf<HTMLSpanElement>;
    private depth3ActivatedTab: HTMLSpanElement | null;
    private depth3ActivatedTabIndex: number;
    private depth3ActivatedTabIndexByDepth4: number;
    private depth3UrlList: Array<string | null>;

    // depth4
    private depth4Tab: HTMLDivElement | null;
    private depth4List: NodeListOf<HTMLUListElement>;
    private depth4TabListItems: NodeListOf<HTMLLIElement>;
    private depth4ActivatedTab: HTMLLIElement | null;
    private depth4ActivatedTabIndex: number;
    private depth4ActivatedSubTabIndex: number;
    private depth4UrlList: depth4UrListType;

    private currentPageUrl: string;
    private isRTLService: boolean;

    constructor() {
        // console.log('-------Tab START-------');
        this.depth3Tab = document.querySelector('.depth3-tab');
        this.depth3List = document.querySelector('.depth3-list');
        this.depth3ListId = this.depth3Tab?.getAttribute('id');
        this.depth3TabListItems = document.querySelectorAll<HTMLSpanElement>('.depth3-list-items');
        this.depth3ActivatedTab = null;
        this.depth3ActivatedTabIndex = -1;
        this.depth3ActivatedTabIndexByDepth4 = -1;
        this.depth3UrlList = [];

        this.depth4Tab = document.querySelector('.depth4-tab');
        this.depth4List = document.querySelectorAll<HTMLUListElement>('.depth4-list');
        this.depth4TabListItems = document.querySelectorAll<HTMLLIElement>('.depth4-list-items');
        this.depth4ActivatedTab = null;
        this.depth4ActivatedTabIndex = -1;
        this.depth4ActivatedSubTabIndex = -1;
        this.depth4UrlList = {};

        this.currentPageUrl = location.pathname;
        this.isRTLService = document.getElementsByTagName('html')[0].getAttribute('dir') === 'rtl';
        this.init();
    }

    init() {
        if (!this.depth3Tab) return;
        this.addClassByDepth3Length();
        this.formattingUrlData();
        this.findActivatedTabIndex();
        this.activateTab();

        if (this.depth3List && this.depth3ActivatedTab)
            this.setEventHandlerAndScroll(this.depth3List, this.depth3ActivatedTab, this.depth3ActivatedTabIndex);
        if (this.depth4Tab && this.depth4ActivatedTab) {
            this.setEventHandlerAndScroll(
                this.depth4List[this.depth4ActivatedTabIndex],
                this.depth4ActivatedTab,
                this.depth4ActivatedSubTabIndex
            );
        }
    }

    // 탭 길이에 따른 class 추가
    private addClassByDepth3Length(): void {
        if (this.depth3TabListItems.length <= 1) return;
        this.depth3Tab?.classList.add(`tab-column-${this.depth3TabListItems.length}`);
    }

    private formattingUrlData(): void {
        this.depth3TabListItems.forEach((depth3TabListItem: HTMLSpanElement) => {
            const urlString = depth3TabListItem.children[0].getAttribute('href');
            if (this.depth3ListId === 'profileTab') {
                this.depth3UrlList.push(urlString ? urlString.split('?')[0] : '');
            } else {
                this.depth3UrlList.push(depth3TabListItem.children[0].getAttribute('href'));
            }
        });

        // 4뎁스 탭이 있을 때 객체 생성
        if (!this.depth4Tab) return;
        this.depth4TabListItems.forEach((depth4TabListItem) => {
            const dataURL = depth4TabListItem.closest('.depth4-list-wrap')?.getAttribute('data-url');
            const key = dataURL === '' ? '/' : dataURL;
            if (!key) return;
            if (!this.depth4UrlList[key]) {
                this.depth4UrlList[key] = [depth4TabListItem.children[0].getAttribute('href')];
            } else {
                this.depth4UrlList[key].push(depth4TabListItem.children[0].getAttribute('href')!);
            }
        });
    }

    // 현재 URL과 비교해서 활성화된 탭 index 찾기
    private findActivatedTabIndex(): void {
        if (this.depth3ListId === 'profileTab') {
            this.currentPageUrl = this.currentPageUrl.split('/')[this.currentPageUrl.split('/').length - 1];
        }
        this.depth3ActivatedTabIndex = this.depth3UrlList.indexOf(this.currentPageUrl);

        Object.entries(this.depth4UrlList)?.forEach(([key, value], index) => {
            if (value.indexOf(this.currentPageUrl) !== -1) {
                this.depth3ActivatedTabIndexByDepth4 = this.depth3UrlList.indexOf(key); // 뎁스4가 속한 뎁스3 Index
                this.depth4ActivatedTabIndex = index; // 활성화된 뎁스4 부모 Index
                this.depth4ActivatedSubTabIndex = value.indexOf(this.currentPageUrl); // 활성화된 뎁스4 자식 Index
                return false;
            }
        });
    }

    // 탭 활성화하기
    private activateTab(): void {
        // 3 뎁스가 활성화 되었다면
        if (this.depth3ActivatedTabIndex !== -1) {
            this._activateDepth3Tab(this.depth3ActivatedTabIndex);
            // 4 뎁스가 존재한다면
            if (this.depth4ActivatedSubTabIndex !== -1) {
                this._activateDepth4Tab();
            }
        } else if (this.depth4ActivatedSubTabIndex !== -1) {
            // 4뎁스 세부 탭 클릭 시
            this._activateDepth3Tab(this.depth3ActivatedTabIndexByDepth4);
            this._activateDepth4Tab();
        }
    }

    private _activateDepth3Tab(activatedIndex: number): void {
        this.depth3List?.children[activatedIndex].classList.add('selected');
        this.depth3ActivatedTab = document.querySelector('.depth3-list-items.selected');
    }

    private _activateDepth4Tab(): void {
        this.depth4Tab?.children[this.depth4ActivatedTabIndex].classList.add('is-show');
        this.depth4List[this.depth4ActivatedTabIndex].children[this.depth4ActivatedSubTabIndex].classList.add(
            'selected'
        );
        this.depth4ActivatedTab = document.querySelector('.depth4-list-items.selected');
    }

    // 새로 고침시 활성화된 탭으로 스크롤
    private scrollToActivatedTab(
        activatedTab: HTMLElement,
        activatedTabIndex: number,
        toBeScrolledElement: HTMLElement
    ): void {
        if (
            window.innerWidth > PC_BREAK_POINT ||
            !(activatedTab instanceof HTMLElement) ||
            !(toBeScrolledElement instanceof HTMLElement)
        )
            return;
        toBeScrolledElement.scrollLeft = getTabWidth(activatedTab, activatedTabIndex, 1, this.isRTLService);
    }

    private setEventHandlerAndScroll(tabList: HTMLElement, activatedTab: HTMLElement, activatedTabIndex: number): void {
        setHorizontalSrollEvent(tabList);
        setResizeEvent(tabList, activatedTab, this.isRTLService, activatedTabIndex);
        this.scrollToActivatedTab(activatedTab, activatedTabIndex, tabList);
    }
} // end of class
